import { useCurrentStateAndParams, useRouter } from '@uirouter/react';
import { AlertTriangle } from 'lucide-react';

import { AddonHealthStatus } from '@api/types.gen';

import { notifySuccess } from '@/portainer/services/notifications';

import { PageHeader } from '@@/PageHeader';
import { Tab, WidgetTabs, useCurrentTabIndex } from '@@/Widget/WidgetTabs';
import { Badge } from '@@/Badge';
import { Icon } from '@@/Icon';

import { useAddon } from '../queries/useAddon';
import {
  confirmAddonUninstall,
  useUninstallAddon,
} from '../queries/useUninstallAddon';
import { confirmAddonRepair, useRepairAddon } from '../queries/useRepairAddon';

import { useAddonReleaseScope } from './queries/useAddonReleaseScope';
import { ConfigurationTab } from './ConfigurationTab';
import { UserAccessTab } from './UserAccessTab';
import { AddonHeader } from './AddonHeader';
import { AddonResourcesTab } from './AddonResourcesTab';
import { AddonEventsTab } from './AddonEventsTab';
import { AddonLogsTab } from './AddonLogsTab';
import { useAddonEventWarningsCount } from './useAddonEventWarningsCount';
import { useAddonRestart, confirmAddonRestart } from './useAddonRestart';

export function AddonDetailView() {
  const router = useRouter();
  const { params } = useCurrentStateAndParams();
  const id = String(params.id ?? '');
  const addonQuery = useAddon(id);
  const addon = addonQuery.data;
  const displayName = addon?.displayName ?? 'Add-on';

  const uninstallMutation = useUninstallAddon(addon?.id ?? '');

  // Surfaces a reinstall action in the header when the Helm release can't be
  // loaded even though the record still reports the add-on as enabled. The scope
  // also tells whether the add-on's namespace exists, which gates the restart.
  const { scope, query: releaseQuery } = useAddonReleaseScope(id);
  const namespaceExists = scope === 'release' || scope === 'namespaceOnly';
  const restartMutation = useAddonRestart(id);
  const repairMutation = useRepairAddon(id);

  const eventWarningCount = useAddonEventWarningsCount(id);
  const tabs = buildTabs(eventWarningCount);
  const currentTabIndex = useCurrentTabIndex(tabs);

  return (
    <>
      <PageHeader
        breadcrumbs={[
          { label: 'Add-on management', link: 'portainer.addons' },
          displayName,
        ]}
        reload
      />
      <div className="row">
        <div className="col-sm-12">
          <AddonHeader
            addon={addon}
            isLoading={addonQuery.isLoading}
            uninstallLoading={uninstallMutation.isLoading}
            releaseFailedToLoad={releaseQuery.isError}
            onUninstall={handleUninstall}
            showRestart={namespaceExists && !!addon?.lifecycleStatus}
            restartLoading={restartMutation.isLoading}
            onRestart={handleRestart}
            showRepair={
              addon?.healthStatus ===
              AddonHealthStatus.ADDON_HEALTH_CREDENTIAL_INVALID
            }
            repairLoading={repairMutation.isLoading}
            onRepair={handleRepair}
          />
        </div>
      </div>
      <WidgetTabs tabs={tabs} currentTabIndex={currentTabIndex} />
      {tabs[currentTabIndex].widget}
    </>
  );

  // The confirmation also carries the choice of whether to discard the add-on's
  // stored settings, so it is owned here rather than by the button. On success we
  // return to the list — the add-on is mid-removal and has no detail left to show.
  async function handleUninstall() {
    const options = await confirmAddonUninstall(displayName);
    if (!options) {
      return;
    }

    uninstallMutation.mutate(options, {
      onSuccess: () => {
        notifySuccess('Success', 'Add-on uninstall started');
        router.stateService.go('portainer.addons');
      },
    });
  }

  // The restart is quick (no async lifecycle), so send the user to the Resources
  // tab to watch the pods roll.
  async function handleRestart() {
    if (await confirmAddonRestart(displayName)) {
      restartMutation.mutate(undefined, {
        onSuccess: () => router.stateService.go('.', { tab: 'resources' }),
      });
    }
  }

  // No navigation: the add-on recovers in place and the badge polls.
  async function handleRepair() {
    if (await confirmAddonRepair(displayName)) {
      repairMutation.mutate();
    }
  }
}

function buildTabs(eventWarningCount: number): Array<Tab> {
  return [
    {
      name: 'Configuration',
      widget: <ConfigurationTab />,
      selectedTabParam: 'configuration',
    },
    {
      name: 'Team access',
      widget: <UserAccessTab />,
      selectedTabParam: 'user-access',
    },
    {
      name: 'Resources',
      widget: <AddonResourcesTab />,
      selectedTabParam: 'resources',
    },
    {
      name: (
        <div className="flex items-center gap-x-2">
          Events
          {eventWarningCount >= 1 && (
            <Badge type="warnSecondary">
              <Icon icon={AlertTriangle} className="!mr-1" />
              {eventWarningCount}
            </Badge>
          )}
        </div>
      ),
      widget: <AddonEventsTab />,
      selectedTabParam: 'events',
    },
    {
      name: 'Logs',
      widget: <AddonLogsTab />,
      selectedTabParam: 'logs',
    },
  ];
}
